import { t as trustWalletConfig } from './chunk2.js';
import { walletConnect as walletConnect$1 } from 'wagmi/connectors';
import { jsxs, jsx } from 'react/jsx-runtime';
import { getAddress, UserRejectedRequestError, ResourceUnavailableRpcError, withTimeout, withRetry, SwitchChainError, numberToHex } from 'viem';
import { createConnector, ProviderNotFoundError, ChainNotConfiguredError } from 'wagmi';

function getEvmInjectedProvider(flag) {
  if (typeof window === "undefined" || typeof window.ethereum === "undefined")
    return;
  const providers = window.ethereum.providers;
  return providers ? providers.find((provider) => provider[flag]) : window.ethereum[flag] ? window.ethereum : void 0;
}
function hasEvmInjectedProvider(flag) {
  return Boolean(getEvmInjectedProvider(flag));
}

function isAndroid() {
  return typeof navigator !== "undefined" && /android/i.test(navigator.userAgent);
}
function isSmallIOS() {
  return typeof navigator !== "undefined" && /iPhone|iPod/.test(navigator.userAgent);
}
function isLargeIOS() {
  return typeof navigator !== "undefined" && (/iPad/.test(navigator.userAgent) || navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
function isIOS() {
  return isSmallIOS() || isLargeIOS();
}
function isMobile() {
  return isAndroid() || isIOS();
}
function isTMA() {
  if (typeof window === "undefined") {
    return false;
  }
  const check = (host) => {
    return typeof host !== "undefined" && "TelegramWebviewProxy" in host && "postEvent" in host.TelegramWebviewProxy && typeof host.TelegramWebviewProxy.postEvent === "function";
  };
  return check(window);
}

async function sleep(duration = 1e3) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, duration);
  });
}
async function openLink(uri, target = "_self") {
  if (!uri)
    return;
  const finalTarget = isTMA() ? "_blank" : target;
  window.open(uri, finalTarget, "noopener noreferrer");
}

function getRecentConnectorId() {
  if (typeof window === "undefined")
    return;
  const recentConnectorId = window.localStorage.getItem("wagmi.recentConnectorId");
  if (recentConnectorId)
    return JSON.parse(recentConnectorId);
}
injected.type = "injected";
function injected(parameters = {}) {
  const { shimDisconnect = true, unstable_shimAsyncInject } = parameters;
  function getTarget() {
    const target = parameters.target;
    if (typeof target === "function") {
      const result = target();
      if (result)
        return result;
    }
    if (typeof target === "object")
      return target;
    if (typeof target === "string")
      return {
        ...targetMap[target] ?? {
          id: target,
          name: `${target[0].toUpperCase()}${target.slice(1)}`,
          provider: `is${target[0].toUpperCase()}${target.slice(1)}`
        }
      };
    return {
      id: "injected",
      name: "Injected",
      async provider(window2) {
        return window2?.ethereum;
      }
    };
  }
  let accountsChanged;
  let chainChanged;
  let connect;
  let disconnect;
  return createConnector((config) => ({
    get icon() {
      return getTarget().icon;
    },
    get id() {
      return getTarget().id;
    },
    get name() {
      return getTarget().name;
    },
    /** @deprecated */
    get supportsSimulation() {
      return true;
    },
    type: injected.type,
    async setup() {
      const provider = await this.getProvider();
      if (provider?.on && parameters.target) {
        if (!connect) {
          connect = this.onConnect.bind(this);
          provider.on("connect", connect);
        }
        if (!accountsChanged) {
          accountsChanged = this.onAccountsChanged.bind(this);
          provider.on("accountsChanged", accountsChanged);
        }
      }
    },
    async connect({ chainId, isReconnecting } = {}) {
      const provider = await this.getProvider();
      if (!provider)
        throw new ProviderNotFoundError();
      let accounts = [];
      if (isReconnecting)
        accounts = await this.getAccounts().catch(() => []);
      else if (shimDisconnect) {
        try {
          const permissions = await provider.request({
            method: "wallet_requestPermissions",
            params: [{ eth_accounts: {} }]
          });
          accounts = permissions[0]?.caveats?.[0]?.value?.map((x) => getAddress(x));
          if (accounts.length > 0) {
            const sortedAccounts = await this.getAccounts();
            accounts = sortedAccounts;
          }
        } catch (err) {
          const error = err;
          if (error.code === UserRejectedRequestError.code)
            throw new UserRejectedRequestError(error);
          if (error.code === ResourceUnavailableRpcError.code)
            throw error;
        }
      }
      try {
        if (!accounts?.length && !isReconnecting) {
          const requestedAccounts = await provider.request({
            method: "eth_requestAccounts"
          });
          accounts = requestedAccounts.map((x) => getAddress(x));
        }
        if (connect) {
          provider.removeListener("connect", connect);
          connect = void 0;
        }
        if (!accountsChanged) {
          accountsChanged = this.onAccountsChanged.bind(this);
          provider.on("accountsChanged", accountsChanged);
        }
        if (!chainChanged) {
          chainChanged = this.onChainChanged.bind(this);
          provider.on("chainChanged", chainChanged);
        }
        if (!disconnect) {
          disconnect = this.onDisconnect.bind(this);
          provider.on("disconnect", disconnect);
        }
        let currentChainId = await this.getChainId();
        if (chainId && currentChainId !== chainId) {
          const chain = await this.switchChain({ chainId }).catch((error) => {
            if (error.code === UserRejectedRequestError.code)
              throw error;
            return { id: currentChainId };
          });
          currentChainId = chain?.id ?? currentChainId;
        }
        if (shimDisconnect)
          await config.storage?.removeItem(`${this.id}.disconnected`);
        if (!parameters.target)
          await config.storage?.setItem("injected.connected", true);
        return { accounts, chainId: currentChainId };
      } catch (err) {
        const error = err;
        if (error.code === UserRejectedRequestError.code)
          throw new UserRejectedRequestError(error);
        if (error.code === ResourceUnavailableRpcError.code)
          throw new ResourceUnavailableRpcError(error);
        throw error;
      }
    },
    async disconnect() {
      const provider = await this.getProvider();
      if (!provider)
        throw new ProviderNotFoundError();
      if (chainChanged) {
        provider.removeListener("chainChanged", chainChanged);
        chainChanged = void 0;
      }
      if (disconnect) {
        provider.removeListener("disconnect", disconnect);
        disconnect = void 0;
      }
      if (!connect) {
        connect = this.onConnect.bind(this);
        provider.on("connect", connect);
      }
      try {
        if (this.id !== "trust") {
          await withTimeout(
            () => (
              // TODO: Remove explicit type for viem@3
              provider.request({
                // `'wallet_revokePermissions'` added in `viem@2.10.3`
                method: "wallet_revokePermissions",
                params: [{ eth_accounts: {} }]
              })
            ),
            { timeout: 100 }
          );
        }
      } catch {
      }
      if (shimDisconnect) {
        await config.storage?.setItem(`${this.id}.disconnected`, true);
      }
      if (!parameters.target)
        await config.storage?.removeItem("injected.connected");
    },
    async getAccounts() {
      const provider = await this.getProvider();
      if (!provider)
        throw new ProviderNotFoundError();
      const accounts = await provider.request({ method: "eth_accounts" });
      return accounts.map((x) => getAddress(x));
    },
    async getChainId() {
      const provider = await this.getProvider();
      if (!provider)
        throw new ProviderNotFoundError();
      const hexChainId = await provider.request({ method: "eth_chainId" });
      return Number(hexChainId);
    },
    async getProvider() {
      if (typeof window === "undefined")
        return void 0;
      let provider;
      const target = getTarget();
      if (typeof target.provider === "function")
        provider = await target.provider(window);
      else if (typeof target.provider === "string")
        provider = findProvider(window, target.provider);
      else
        provider = target.provider;
      if (provider && !provider.removeListener) {
        if ("off" in provider && typeof provider.off === "function")
          provider.removeListener = provider.off;
        else
          provider.removeListener = () => {
          };
      }
      return provider;
    },
    async isAuthorized() {
      try {
        const isRecentConnector = getRecentConnectorId() === this.id;
        const disconnected = await config.storage?.getItem(`${this.id}.disconnected`);
        const isDisconnected = !shimDisconnect || !isRecentConnector || disconnected === true;
        if (isDisconnected)
          return false;
        if (!parameters.target) {
          const connected = await config.storage?.getItem("injected.connected");
          if (!connected)
            return false;
        }
        const provider = await this.getProvider();
        if (!provider) {
          if (unstable_shimAsyncInject !== void 0 && unstable_shimAsyncInject !== false) {
            const handleEthereum = async () => {
              if (typeof window !== "undefined")
                window.removeEventListener("ethereum#initialized", handleEthereum);
              const provider2 = await this.getProvider();
              return !!provider2;
            };
            const timeout = typeof unstable_shimAsyncInject === "number" ? unstable_shimAsyncInject : 1e3;
            const res = await Promise.race([
              ...typeof window !== "undefined" ? [
                new Promise(
                  (resolve) => window.addEventListener(
                    "ethereum#initialized",
                    () => resolve(handleEthereum()),
                    { once: true }
                  )
                )
              ] : [],
              new Promise(
                (resolve) => setTimeout(() => resolve(handleEthereum()), timeout)
              )
            ]);
            if (res)
              return true;
          }
          throw new ProviderNotFoundError();
        }
        const accounts = await withRetry(() => this.getAccounts());
        return !!accounts.length;
      } catch {
        return false;
      }
    },
    async switchChain({ addEthereumChainParameter, chainId }) {
      const provider = await this.getProvider();
      if (!provider)
        throw new ProviderNotFoundError();
      const chain = config.chains.find((x) => x.id === chainId);
      if (!chain)
        throw new SwitchChainError(new ChainNotConfiguredError());
      const promise = new Promise((resolve) => {
        const listener = (data) => {
          if ("chainId" in data && data.chainId === chainId) {
            config.emitter.off("change", listener);
            resolve();
          }
        };
        config.emitter.on("change", listener);
      });
      try {
        await Promise.all([
          provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: numberToHex(chainId) }]
          }).then(async () => {
            const currentChainId = await this.getChainId();
            if (currentChainId === chainId)
              config.emitter.emit("change", { chainId });
          }),
          promise
        ]);
        return chain;
      } catch (err) {
        const error = err;
        if (error.code === 4902 || // Unwrapping for MetaMask Mobile
        // https://github.com/MetaMask/metamask-mobile/issues/2944#issuecomment-976988719
        error?.data?.originalError?.code === 4902) {
          try {
            const { default: blockExplorer, ...blockExplorers } = chain.blockExplorers ?? {};
            let blockExplorerUrls;
            if (addEthereumChainParameter?.blockExplorerUrls)
              blockExplorerUrls = addEthereumChainParameter.blockExplorerUrls;
            else if (blockExplorer)
              blockExplorerUrls = [
                blockExplorer.url,
                ...Object.values(blockExplorers).map((x) => x.url)
              ];
            let rpcUrls;
            if (addEthereumChainParameter?.rpcUrls?.length)
              rpcUrls = addEthereumChainParameter.rpcUrls;
            else
              rpcUrls = [chain.rpcUrls.default?.http[0] ?? ""];
            const addEthereumChain = {
              blockExplorerUrls,
              chainId: numberToHex(chainId),
              chainName: addEthereumChainParameter?.chainName ?? chain.name,
              iconUrls: addEthereumChainParameter?.iconUrls,
              nativeCurrency: addEthereumChainParameter?.nativeCurrency ?? chain.nativeCurrency,
              rpcUrls
            };
            await Promise.all([
              provider.request({
                method: "wallet_addEthereumChain",
                params: [addEthereumChain]
              }).then(async () => {
                const currentChainId = await this.getChainId();
                if (currentChainId === chainId)
                  config.emitter.emit("change", { chainId });
                else
                  throw new UserRejectedRequestError(
                    new Error("User rejected switch after adding network.")
                  );
              }),
              promise
            ]);
            return chain;
          } catch (error2) {
            throw new UserRejectedRequestError(error2);
          }
        }
        if (error.code === UserRejectedRequestError.code)
          throw new UserRejectedRequestError(error);
        throw new SwitchChainError(error);
      }
    },
    async onAccountsChanged(accounts) {
      const isRecentConnector = getRecentConnectorId() === this.id;
      if (this.id === "trust" && !isRecentConnector)
        return;
      if (accounts.length === 0)
        this.onDisconnect();
      else if (config.emitter.listenerCount("connect")) {
        const chainId = (await this.getChainId()).toString();
        this.onConnect({ chainId });
        if (shimDisconnect)
          await config.storage?.removeItem(`${this.id}.disconnected`);
      } else
        config.emitter.emit("change", {
          accounts: accounts.map((x) => getAddress(x))
        });
    },
    onChainChanged(chain) {
      const chainId = Number(chain);
      config.emitter.emit("change", { chainId });
    },
    async onConnect(connectInfo) {
      const accounts = await this.getAccounts();
      if (accounts.length === 0)
        return;
      const chainId = Number(connectInfo.chainId);
      config.emitter.emit("connect", { accounts, chainId });
      const provider = await this.getProvider();
      if (provider) {
        if (connect) {
          provider.removeListener("connect", connect);
          connect = void 0;
        }
        if (!accountsChanged) {
          accountsChanged = this.onAccountsChanged.bind(this);
          provider.on("accountsChanged", accountsChanged);
        }
        if (!chainChanged) {
          chainChanged = this.onChainChanged.bind(this);
          provider.on("chainChanged", chainChanged);
        }
        if (!disconnect) {
          disconnect = this.onDisconnect.bind(this);
          provider.on("disconnect", disconnect);
        }
      }
    },
    async onDisconnect(error) {
      const provider = await this.getProvider();
      if (error && error.code === 1013) {
        if (provider && !!(await this.getAccounts()).length)
          return;
      }
      config.emitter.emit("disconnect");
      if (provider) {
        if (chainChanged) {
          provider.removeListener("chainChanged", chainChanged);
          chainChanged = void 0;
        }
        if (disconnect) {
          provider.removeListener("disconnect", disconnect);
          disconnect = void 0;
        }
        if (!connect) {
          connect = this.onConnect.bind(this);
          provider.on("connect", connect);
        }
      }
    }
  }));
}
const targetMap = {
  coinbaseWallet: {
    id: "coinbaseWallet",
    name: "Coinbase Wallet",
    async provider(window2) {
      if (window2?.coinbaseWalletExtension)
        return window2.coinbaseWalletExtension;
      return findProvider(window2, "isCoinbaseWallet");
    }
  },
  metaMask: {
    id: "metaMask",
    name: "MetaMask",
    async provider(window2) {
      return findProvider(window2, (provider) => {
        if (!provider.isMetaMask)
          return false;
        if (provider.isBraveWallet && !provider._events && !provider._state)
          return false;
        const flags = [
          "isApexWallet",
          "isAvalanche",
          "isBitKeep",
          "isBlockWallet",
          "isKuCoinWallet",
          "isMathWallet",
          "isOkxWallet",
          "isOKExWallet",
          "isOneInchIOSWallet",
          "isOneInchAndroidWallet",
          "isOpera",
          "isPortal",
          "isRabby",
          "isTokenPocket",
          "isTokenary",
          "isUniswapWallet",
          "isZerion"
        ];
        for (const flag of flags)
          if (provider[flag])
            return false;
        return true;
      });
    }
  },
  phantom: {
    id: "phantom",
    name: "Phantom",
    async provider(window2) {
      if (window2?.phantom?.ethereum)
        return window2.phantom?.ethereum;
      return findProvider(window2, "isPhantom");
    }
  }
};
function findProvider(window2, select) {
  function isProvider(provider) {
    if (typeof select === "function")
      return select(provider);
    if (typeof select === "string")
      return provider[select];
    return true;
  }
  const ethereum = window2.ethereum;
  if (ethereum?.providers)
    return ethereum.providers.find((provider) => isProvider(provider));
  if (ethereum && isProvider(ethereum))
    return ethereum;
  return void 0;
}

function trustWallet(props = {}) {
  const { connectorOptions, ...restProps } = props;
  return {
    ...trustWalletConfig,
    id: "trust",
    walletType: "evm",
    showQRCode: false,
    isInstalled() {
      return !!getProvider$1();
    },
    getDeepLink() {
      const encodedUrl = encodeURIComponent(window.location.href);
      return `https://link.trustwallet.com/open_url?coin_id=60&url=${encodedUrl}`;
    },
    getUri(uri) {
      let encodedUri = encodeURIComponent(uri);
      if (isTMA() && isAndroid()) {
        encodedUri = encodeURIComponent(encodedUri);
      }
      return `https://link.trustwallet.com/wc?uri=${encodedUri}`;
    },
    getCreateConnectorFn() {
      let isReady = false;
      return injected({
        shimDisconnect: true,
        target: {
          id: trustWallet().id,
          name: trustWallet().name,
          async provider() {
            if (!isReady) {
              await sleep();
            }
            isReady = true;
            return getProvider$1();
          }
        },
        ...connectorOptions
      });
    },
    ...restProps
  };
}
function getProvider$1() {
  if (typeof window === "undefined")
    return;
  if (!window?.trustwallet?.request)
    return;
  return window.trustwallet ?? window.trustWallet ?? getEvmInjectedProvider("isTrust");
}

const WalletConnectTransparentIcon = (props) => {
  return /* @__PURE__ */ jsxs("svg", { width: "40", height: "40", viewBox: "0 0 40 40", fill: "none", ...props, children: [
    /* @__PURE__ */ jsx("g", { clipPath: "url(#clip0_3705_10415)", children: /* @__PURE__ */ jsx(
      "path",
      {
        d: "M11.9465 14.9229C16.3942 10.581 23.605 10.581 28.0527 14.9229L28.5879 15.4454C28.8103 15.6625 28.8103 16.0145 28.5879 16.2316L26.7568 18.0191C26.6457 18.1276 26.4654 18.1276 26.3542 18.0191L25.6176 17.3C22.5149 14.271 17.4843 14.271 14.3816 17.3L13.5928 18.0701C13.4816 18.1786 13.3013 18.1786 13.1901 18.0701L11.359 16.2826C11.1367 16.0655 11.1367 15.7135 11.359 15.4964L11.9465 14.9229ZM31.8395 18.6197L33.4691 20.2105C33.6915 20.4276 33.6915 20.7796 33.4691 20.9967L26.1209 28.1703C25.8986 28.3874 25.538 28.3874 25.3156 28.1703L20.1003 23.079C20.0447 23.0247 19.9546 23.0247 19.899 23.079L14.6838 28.1703C14.4614 28.3874 14.1008 28.3874 13.8785 28.1703L6.53007 20.9966C6.30769 20.7795 6.30769 20.4276 6.53007 20.2105L8.15972 18.6196C8.38209 18.4025 8.74264 18.4025 8.96502 18.6196L14.1804 23.7109C14.236 23.7652 14.3261 23.7652 14.3817 23.7109L19.5969 18.6196C19.8192 18.4025 20.1798 18.4025 20.4021 18.6195L25.6175 23.7109C25.6732 23.7652 25.7633 23.7652 25.8188 23.7109L31.0342 18.6197C31.2565 18.4026 31.6171 18.4026 31.8395 18.6197Z",
        fill: "#3396FF"
      }
    ) }),
    /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx("clipPath", { id: "clip0_3705_10415", children: /* @__PURE__ */ jsx("rect", { width: "40", height: "40", fill: "white" }) }) })
  ] });
};
const WalletConnectIcon = (props) => {
  return /* @__PURE__ */ jsxs("svg", { width: "40", height: "40", viewBox: "0 0 68 68", fill: "none", ...props, children: [
    /* @__PURE__ */ jsx("rect", { width: "68", height: "68", rx: "16", fill: "#3396FF" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M20.0995 25.2382C27.7768 17.7435 40.224 17.7435 47.9013 25.2382L48.8252 26.1402C49.209 26.5149 49.209 27.1225 48.8252 27.4972L45.6645 30.5828C45.4726 30.7702 45.1614 30.7702 44.9694 30.5828L43.6979 29.3416C38.3422 24.113 29.6586 24.113 24.3029 29.3416L22.9412 30.6708C22.7493 30.8582 22.438 30.8582 22.2461 30.6708L19.0854 27.5853C18.7016 27.2105 18.7016 26.603 19.0854 26.2282L20.0995 25.2382ZM54.4379 31.6195L57.2509 34.3655C57.6348 34.7403 57.6348 35.3478 57.2509 35.7226L44.5667 48.1054C44.1829 48.4801 43.5606 48.4801 43.1766 48.1054L34.1742 39.317C34.0782 39.2233 33.9227 39.2233 33.8267 39.317L24.8244 48.1054C24.4406 48.4801 23.8182 48.4801 23.4344 48.1054L10.7498 35.7225C10.3659 35.3477 10.3659 34.7402 10.7498 34.3654L13.5629 31.6193C13.9467 31.2446 14.5691 31.2446 14.9529 31.6193L23.9556 40.4077C24.0515 40.5015 24.207 40.5015 24.303 40.4077L33.3052 31.6193C33.689 31.2446 34.3114 31.2446 34.6952 31.6192L43.6978 40.4077C43.7939 40.5015 43.9494 40.5015 44.0453 40.4077L53.0478 31.6195C53.4316 31.2447 54.054 31.2447 54.4379 31.6195Z",
        fill: "white"
      }
    )
  ] });
};

const walletConnectConfig = {
  name: "WalletConnect",
  logos: {
    default: /* @__PURE__ */ jsx(WalletConnectIcon, {}),
    transparent: /* @__PURE__ */ jsx(WalletConnectTransparentIcon, {})
  },
  downloadUrls: {
    default: void 0
  }
};

let evmGlobalData = {};
const setEvmGlobalData = (value) => {
  evmGlobalData = {
    ...evmGlobalData,
    ...value
  };
};
const getEvmGlobalData = () => {
  return evmGlobalData;
};

function walletConnect(props = {}) {
  const { connectorOptions, ...restProps } = props;
  return {
    ...walletConnectConfig,
    id: "walletConnect",
    walletType: "evm",
    showQRCode: !connectorOptions?.showQrModal,
    isInstalled() {
      return false;
    },
    getDeepLink() {
      return void 0;
    },
    getUri(uri) {
      return uri;
    },
    getCreateConnectorFn() {
      const { walletConnectProjectId, metadata } = getEvmGlobalData();
      const hasAllAppData = metadata?.name && metadata.icon && metadata.description && metadata.url;
      if (!walletConnectProjectId) {
        throw new Error("walletConnectProjectId is required.");
      }
      return walletConnect$1({
        // https://github.com/WalletConnect/walletconnect-monorepo/issues/2830
        relayUrl: "wss://relay.walletconnect.org",
        projectId: walletConnectProjectId,
        metadata: hasAllAppData ? {
          name: metadata.name,
          description: metadata.description,
          url: metadata.url,
          icons: [metadata.icon]
        } : void 0,
        qrModalOptions: {
          explorerRecommendedWalletIds: [
            "8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4",
            "c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96",
            "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0"
          ]
        },
        ...connectorOptions,
        showQrModal: true
      });
    },
    ...restProps
  };
}
function isWalletConnect(id) {
  return id === walletConnect().id;
}

const BinanceWalletTransparentIcon = (props) => {
  return /* @__PURE__ */ jsx(BinanceWalletIcon, { width: 40, height: 40, ...props });
};
const BinanceWalletIcon = (props) => {
  return /* @__PURE__ */ jsxs("svg", { width: "40", height: "40", viewBox: "0 0 68 68", fill: "none", ...props, children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M0 18.1333C0 8.11857 8.11857 0 18.1333 0H49.8667C59.8814 0 68 8.11857 68 18.1333V49.8667C68 59.8814 59.8814 68 49.8667 68H18.1333C8.11857 68 0 59.8814 0 49.8667V18.1333Z",
        fill: "#0B0E11"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M24.7562 30.201L34.0001 20.9572L43.2487 30.2059L48.6274 24.8269L34.0001 10.1992L19.3774 24.8221L24.7562 30.201ZM15.5792 28.6199L20.958 33.9987L15.5789 39.3778L10.2002 33.999L15.5792 28.6199ZM24.7562 37.7973L34.0001 47.0409L43.2485 37.7927L48.6303 43.1688L48.6274 43.1715L34.0001 57.7992L19.3772 43.1763L19.3697 43.1689L24.7562 37.7973ZM57.8002 33.9997L52.4215 39.3785L47.0427 33.9997L52.4215 28.6209L57.8002 33.9997Z",
        fill: "#F0B90B"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M39.4555 33.9965H39.458L33.9999 28.5382L28.5391 33.9991L28.5465 34.0067L33.9999 39.4601L39.4606 33.9991L39.4555 33.9965Z",
        fill: "#F0B90B"
      }
    )
  ] });
};

const binanceWalletConfig = {
  name: "Binance Wallet",
  logos: {
    default: /* @__PURE__ */ jsx(BinanceWalletIcon, {}),
    transparent: /* @__PURE__ */ jsx(BinanceWalletTransparentIcon, {})
  },
  downloadUrls: {
    default: "https://www.binance.com/en/web3wallet"
  },
  spinnerColor: void 0
};

function binanceWallet(props = {}) {
  const { connectorOptions, ...restProps } = props;
  return {
    ...binanceWalletConfig,
    id: "binanceWallet",
    walletType: "evm",
    showQRCode: true,
    isInstalled() {
      return !!getProvider();
    },
    getDeepLink() {
      const url = window.location.href;
      const base = "bnc://app.binance.com/mp/app";
      const appId = "yFK5FCqYprrXDiVFbhyRx7";
      const startPagePath = window.btoa("/pages/browser/index");
      const startPageQuery = window.btoa(`url=${url}`);
      const deeplink = `${base}?appId=${appId}&startPagePath=${startPagePath}&startPageQuery=${startPageQuery}`;
      const dp = window.btoa(deeplink);
      const http = `https://app.binance.com/en/download?_dp=${dp}`;
      return http;
    },
    getUri(uri) {
      return uri;
    },
    getCreateConnectorFn() {
      let isReady = false;
      return injected({
        shimDisconnect: true,
        target: {
          id: this.id,
          name: binanceWallet().name,
          async provider() {
            if (isMobile() && binanceWallet().isInstalled() && !isReady) {
              await sleep(3e3);
            }
            isReady = true;
            return getProvider();
          }
        },
        ...connectorOptions
      });
    },
    ...restProps
  };
}
function binanceWeb3Wallet(props = {}) {
  return {
    ...binanceWallet(props),
    id: "binanceWeb3Wallet"
  };
}
function getProvider() {
  if (typeof window === "undefined")
    return;
  return getEvmInjectedProvider("isBinance");
}

export { getEvmGlobalData as a, isTMA as b, isAndroid as c, isWalletConnect as d, binanceWallet as e, binanceWeb3Wallet as f, getEvmInjectedProvider as g, hasEvmInjectedProvider as h, injected as i, isMobile as j, isIOS as k, isSmallIOS as l, isLargeIOS as m, openLink as o, setEvmGlobalData as s, trustWallet as t, walletConnect as w };
